/**
 * Exact decimal arithmetic on scaled `bigint`, for the amortisation formulas and nothing else.
 *
 * ============================================================================
 * WHY THIS EXISTS AT ALL
 * ============================================================================
 *
 * This codebase has two established strategies for money, and neither covers what this feature
 * needs.
 *
 *   `Valuation` (@gladis/shared) holds integer cents in a `bigint`. Exact under addition and
 *   subtraction — and it has no multiplication, no division and no exponentiation, so it cannot
 *   express `P × ((1+r)ⁿ − (1+r)ᵐ) / ((1+r)ⁿ − 1)`.
 *
 *   006's rule for aggregates was "sum in Postgres". An outstanding balance is not stored anywhere,
 *   so Postgres has nothing to sum.
 *
 * ============================================================================
 * WHY NOT `number`
 * ============================================================================
 *
 * The magnitude argument is NOT the reason, and pretending otherwise would be dishonest. float64
 * carries ~15–16 significant digits, so a €300,000 annuity computes to within about 1e-10 EUR of
 * the true value — nowhere near a cent.
 *
 * The reason is DETERMINISM. `Math.pow` is not required by the ECMAScript specification to be
 * correctly rounded; it is explicitly implementation-approximated. Two Node builds, or one build on
 * two architectures, may differ in the last bits. A value sitting within that difference of a
 * half-cent boundary then rounds to a DIFFERENT CENT on the two machines — and the household sees a
 * total that disagrees with the sum of its rows on one laptop and agrees on another, with the bug
 * unreproducible wherever it was reported.
 *
 * Integer arithmetic on `bigint` has no such freedom. The same inputs give the same digits
 * everywhere, forever.
 *
 * ============================================================================
 * WHY NOT `decimal.js`
 * ============================================================================
 *
 * Two reasons, either sufficient. It is a runtime dependency, and Principle IV permits a domain
 * package only `@gladis/shared`, `zod` and `neverthrow` — putting it in `@gladis/shared` instead
 * would place it in every domain package's resolved tree while leaving every manifest looking
 * compliant, which is a circumvention rather than a compliance. And its precision is GLOBAL MUTABLE
 * CONFIGURATION: `Decimal.set({ precision })` anywhere changes the result of money arithmetic
 * everywhere, which is the exact opposite of the property this module exists to provide.
 *
 * ============================================================================
 * PRECISION
 * ============================================================================
 *
 * Twenty fractional decimal digits. `(1 + r)` is truncated once at 20 digits (relative error below
 * 1e-18 for a realistic rate), and each multiplication in the iterated power truncates once more.
 * Over a 360-month term the accumulated relative error stays below 4e-18 — on a €10,000,000
 * principal, under 1e-10 EUR, which is eight orders of magnitude below the rounding step.
 *
 * NOT EXPORTED FROM THE PACKAGE. This module is an implementation detail of `amortisation.ts`, and
 * the only money arithmetic it is meant to serve is the two functions tested against an
 * independently produced amortisation table. A second caller doing money arithmetic here would be
 * doing it somewhere nothing checks.
 */

/** Fractional decimal digits carried. See PRECISION above before changing it. */
export const SCALE_DIGITS = 20;

const SCALE = 10n ** BigInt(SCALE_DIGITS);
const HALF_SCALE = SCALE / 2n;

/** A decimal number, held as `value × 10^SCALE_DIGITS`. */
export type Fixed = bigint;

export const ZERO: Fixed = 0n;
export const ONE: Fixed = SCALE;

/** From a whole number. `fromInt(3n)` is exactly 3. */
export function fromInt(value: number | bigint): Fixed {
  return BigInt(value) * SCALE;
}

/**
 * From a decimal string, exactly. `"3.500"` is exactly three and a half.
 *
 * Digits are parsed rather than a float being scaled, so the value is exact by construction rather
 * than by rounding — the same reasoning `Valuation.fromDecimalString` records. Extra fractional
 * digits beyond the scale are truncated, which cannot arise from a `numeric(6,3)` or
 * `numeric(15,2)` column.
 */
export function fromDecimalString(value: string): Fixed {
  const text = value.trim();
  const negative = text.startsWith("-");
  const magnitude = negative ? text.slice(1) : text;
  const [whole = "0", fraction = ""] = magnitude.split(".");
  const padded = `${fraction}${"0".repeat(SCALE_DIGITS)}`.slice(0, SCALE_DIGITS);
  const result = BigInt(whole || "0") * SCALE + BigInt(padded || "0");
  return negative ? -result : result;
}

export function add(a: Fixed, b: Fixed): Fixed {
  return a + b;
}

export function sub(a: Fixed, b: Fixed): Fixed {
  return a - b;
}

/** Truncates toward zero at the scale. */
export function mul(a: Fixed, b: Fixed): Fixed {
  return (a * b) / SCALE;
}

/** Truncates toward zero at the scale. The caller guarantees `b` is non-zero. */
export function div(a: Fixed, b: Fixed): Fixed {
  return (a * SCALE) / b;
}

/**
 * `[base^capture, base^exponent]` from ONE pass, because `capture <= exponent`.
 *
 * The amortisation formulas need `(1+r)ᵐ` and `(1+r)ⁿ` together, and computing them separately would
 * do the shared work twice and — worse — could accumulate truncation differently along two paths,
 * so that `(1+r)ⁿ` computed here and `(1+r)ⁿ` computed there differ in the last digit.
 *
 * A plain loop rather than exponentiation by squaring: `durationMonths` is bounded by the product at
 * a few hundred, so the loop is microseconds, and it is auditable by reading it.
 */
export function powPair(base: Fixed, capture: number, exponent: number): [Fixed, Fixed] {
  let running = ONE;
  let captured = ONE;
  for (let i = 1; i <= exponent; i += 1) {
    running = mul(running, base);
    if (i === capture) captured = running;
  }
  // `capture === 0` leaves `captured` at ONE, which is correct: anything to the power of zero.
  // `capture > exponent` cannot happen — callers clamp — and would silently return ONE, so it is
  // the caller's guarantee rather than this function's.
  return [captured, running];
}

/**
 * To integer cents, rounded HALF-UP (away from zero), applied ONCE at the very end.
 *
 * Rounding at each step of a 360-month amortisation accumulates a visible error, which is why every
 * intermediate value above stays at full scale and only the final figure comes through here.
 *
 * Negatives round away from zero rather than toward it, so the rule is symmetric. No balance or
 * interest figure in this feature is negative, but a rounding function whose behaviour depends on
 * sign is a trap for whoever reuses it.
 */
export function toCentsHalfUp(value: Fixed): bigint {
  const scaled = value * 100n;
  return scaled >= 0n ? (scaled + HALF_SCALE) / SCALE : (scaled - HALF_SCALE) / SCALE;
}

/**
 * To integer cents, rounded UP (ceiling), for the one figure a lender rounds that way.
 *
 * ==========================================================================================
 * WHY THE MONTHLY PAYMENT IS NOT ROUNDED HALF-UP LIKE EVERY OTHER FIGURE
 * ==========================================================================================
 *
 * Because that is not what a lender does, and this household's own statements prove it. Three real
 * mortgage parts, checked against the provider's figures:
 *
 *     part          unrounded    half-up    ceiling    the provider says
 *     121,426 @3.19%  561.715244   561.72     561.72     561.72
 *     134,770 @4.23%  661.410382   661.41     661.42     661.42
 *      75,135 @3.19%  325.063062   325.06     325.07     325.07
 *
 * Half-up matches on one part out of three, and only because that one happens to sit above the half
 * cent. Ceiling matches all three.
 *
 * THE REASON LENDERS DO IT is that a payment rounded down cannot repay the loan: 360 payments each a
 * fraction of a cent short leave a residue at the end of the term. Rounding up overpays by at most a
 * cent a month and the final payment absorbs the difference, which is why an amortisation table from a
 * bank always closes and one built on half-up sometimes does not.
 *
 * INTEREST IS STILL HALF-UP, and the provider's interest figures confirm it to the cent on all three
 * parts. The two rules coexisting is not an inconsistency: interest is a value being measured, and the
 * payment is an instalment being SET.
 */
export function toCentsCeiling(value: Fixed): bigint {
  const scaled = value * 100n;
  const quotient = scaled / SCALE;
  // Integer division truncates toward zero, so a positive value with any remainder needs one more cent.
  if (scaled >= 0n) return scaled % SCALE === 0n ? quotient : quotient + 1n;
  return quotient;
}
